import type { PlanFamily } from './catalog';

// Régimenes that map to one of Heru's three monthly subscription plans.
// `otro` and the "I don't know / not registered yet" sentinels (`no_se`,
// `no_alta`) deliberately aren't here — we don't actually know the régimen, so
// they never count as a plan signal.
export const REGIMES_WITH_MONTHLY_PLAN = new Set([
	'resico',
	'plataformas',
	'empresarial',
	'rif',
	'arrendamiento',
]);

// Every régimen the ladder recognizes: the plan-bearing set plus `sueldos`
// (salaried) — sueldos bears no monthly obligation of its own but DOES place
// the taxpayer in the plataformas family and counts toward complexity.
const KNOWN_REGIMES = new Set([...REGIMES_WITH_MONTHLY_PLAN, 'sueldos']);

const MANY_REGIMES_THRESHOLD = 3;

// Régimen multiselect → recommended monthly plan family, over a GIVEN list of
// régimenes. The user's recommended plan does NOT come from here: it comes from
// heru-user-management-engine (planrecommendation), read by usePlanRegimeFit.
// Only the fiscal-advisor diagnostic and the Ponte al día package still apply
// the rule locally, to régimen lists of their own.
// TODO: move both to POST /plan-recommendation/web/evaluate and delete this copy.
//
// The ladder mirrors the plans catalog tiers:
//   1. rif o empresarial presente, o MÁS de 3 regímenes conocidos → Plan
//      Empresarial (actividad compleja).
//   2. RESICO presente — solo o acompañado — → Plan RESICO ('profesionista').
//   3. Solo sueldos, solo plataformas, o sueldos+plataformas → Plan
//      Plataformas.
//   4. Cualquier otra combinación con un régimen plan-bearing (p.ej.
//      arrendamiento) → Plan RESICO.
//   5. Nada reconocible → null.
export const recommendPlanFromRegimes = (
	regimes: string[],
): PlanFamily | null => {
	const known = new Set(regimes.filter((regime) => KNOWN_REGIMES.has(regime)));

	if (known.has('rif') || known.has('empresarial')) return 'empresarial';
	if (known.size > MANY_REGIMES_THRESHOLD) return 'empresarial';
	if (known.has('resico')) return 'profesionista';

	const isPlataformasFamily =
		known.size > 0 &&
		[...known].every(
			(regime) => regime === 'plataformas' || regime === 'sueldos',
		);
	if (isPlataformasFamily) return 'plataformas';

	const hasPlanBearing = [...known].some((regime) =>
		REGIMES_WITH_MONTHLY_PLAN.has(regime),
	);
	return hasPlanBearing ? 'profesionista' : null;
};
